"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

function MainPostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse">
        <div className="mb-4 h-12 w-3/4 rounded-md bg-muted"></div>
        <div className="mb-8 h-6 w-1/4 rounded-md bg-muted"></div>
        <div className="mb-8 aspect-video w-full rounded-md bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-5/6 rounded-md bg-muted"></div>
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-4/5 rounded-md bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center">
        <h2 className="mb-4 font-default text-2xl font-bold text-foreground">Artículo no encontrado</h2>
        <p className="text-muted-foreground">El contenido solicitado sobre recambios de maquinaria no pudo ser cargado.</p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Volver al Inicio</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl">
      <header className="mb-8 text-center md:text-left">
        <h1 className="mb-4 font-default text-3xl font-bold leading-tight text-foreground md:text-5xl">
          {post.title}
        </h1>
        {post.date && (
          <time className="text-sm font-mono text-primary">
            {new Date(post.date).toLocaleDateString("es-ES", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </time>
        )}
      </header>

      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-md shadow-lg border border-border">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video h-auto w-full object-cover"
          />
        </div>
      )}

      <div
        className="mb-12 space-y-6 text-base leading-relaxed text-foreground/90 font-default [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>h2]:mb-4 [&>h2]:mt-10 [&>h2]:font-default [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-foreground [&>h3]:mb-3 [&>h3]:mt-8 [&>h3]:font-default [&>h3]:text-xl [&>h3]:font-bold [&>h3]:text-foreground [&>ol]:mb-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>p]:mb-4 [&>ul]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&_a]:text-primary [&_a]:underline [&_a]:font-bold"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
      />

      <div className="mt-12 flex flex-col items-center gap-6 rounded-md border border-border bg-card p-6 shadow-md sm:flex-row sm:items-start">
        <Avatar className="h-20 w-20 border-2 border-primary">
          <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
          <AvatarFallback className="bg-secondary text-2xl text-secondary-foreground font-default">
            {post.author?.name?.charAt(0) || "R"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 text-center sm:text-left">
          <h3 className="mb-2 font-default text-xl font-bold text-foreground">{post.author?.name || "Especialista en Recambios"}</h3>
          <p className="text-sm leading-relaxed text-muted-foreground font-default">
            {post.customFields?.author_bio ||
              "Experto en componentes para maquinaria pesada Hitachi, Case y New Holland. Comprometidos con ofrecer soluciones técnicas y recambios de alta calidad para obras públicas."}
          </p>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) return null;

  return (
    <div className="mx-auto mt-20 max-w-4xl border-t border-border pt-12">
      <h2 className="mb-8 text-center font-default text-2xl font-bold text-foreground md:text-left md:text-3xl">
        Otras Ofertas y Artículos Técnicos
      </h2>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
        {posts.slice(0, 3).map((post, i) => (
          <Card
            key={post.id}
            data-index={i}
            className="flex flex-col overflow-hidden border-border bg-card shadow-md transition-all hover:shadow-xl hover:border-primary/50"
          >
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block shrink-0 overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-48 w-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </Link>
            )}
            <CardHeader className="flex-1 pb-2">
              <Link to={post.link || "#"}>
                <CardTitle className="line-clamp-2 font-default text-lg text-foreground transition-colors hover:text-primary">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="pb-4">
              <div
                className="line-clamp-3 text-sm text-muted-foreground font-default"
                dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
              />
            </CardContent>
            <CardFooter className="mt-auto pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button
                  variant="outline"
                  className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default"
                >
                  Ver Detalles
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof wvcClient !== 'undefined' ? wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const mainQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="240773"
      id="single-post-template-section"
      className="bg-background px-4 py-16 text-foreground md:px-8 lg:py-24"
    >
      <PostsProvider wp_query={mainQuery}>
        <MainPostContent />
      </PostsProvider>

      <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
        <RelatedPostsList />
      </PostsProvider>
    </section>
  );
}